import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Avatar,
    Button,
    Typography,
    IconButton,
    Input,
    Textarea,
} from "@material-tailwind/react";
import Select, { components } from "react-select";
import { ArrowLeftIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchAssociatedMembers,
    reassignRfqOrOrder,
    fetchAssociatedManagers,
    fetchMembersByManager
} from "@/store/slices/rfqSlice";
import Toaster from "@/components/Toaster";
import ShowLoader from "@/components/Loader";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const departmentOrder = ["Sales", "Procurement", "City", "Finance"];

const priorities = [
    { label: "High", value: "high" },
    { label: "Medium", value: "medium" },
    { label: "Low", value: "low" },
];

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: "12px",
        minHeight: "48px",
        borderColor: state.isFocused ? "#000" : "#d1d5db",
        boxShadow: "none",
        "&:hover": { borderColor: "#000" },
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999,
        borderRadius: "12px",
        overflow: "hidden",
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? "#f3f4f6" : "#fff",
        color: "#111827",
        paddingTop: 12,
        paddingBottom: 12,
        cursor: "pointer",
    }),
};

const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex =
    /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;

const schema = yup.object().shape({
    member: yup.object().nullable().required("member_required"),
    deadline: yup.string().required("deadline_required"),
    priority: yup.object().nullable().required("priority_required"),
    notes: yup.string()
        .required("reason_notes_required")
        .trim()
        .min(5, "reason_notes_min")
        .max(200, "reason_notes_max")
        .matches(noHTMLRegex, "HTML tags are not allowed")
        .matches(noCodeRegex, "Code content is not allowed"),
});

export default function ReassignRfqModal({
    isOpen,
    onClose,
    rfqId,
    isOrderView = false
}) {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { t } = useTranslation("rfq");
    const { t: ct } = useTranslation("common");

    const {
        loading,
        associated_members,
        associated_managers,
        manager_members
    } = useSelector((state) => state.rfq || {}); // Ensure state.rfq is an object before destructuring

    const [reassignType, setReassignType] = useState("associated");
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [selectedManager, setSelectedManager] = useState(null);

    const {
        control,
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            member: null,
            deadline: "",
            priority: priorities[1],
            notes: "",
        },
    });

    const selectedUser = watch("member");

    useEffect(() => {

        if (!isOpen || !rfqId) return;

        if (reassignType === "associated") {

            dispatch(fetchAssociatedMembers(rfqId))
                .unwrap()
                .catch(() => {
                    Toaster.error(t("failed_to_load_associated_members"));
                });

        } else {

            dispatch(fetchAssociatedManagers({
                rfqId,
                page: 1,
                per_page: 100
            }));
        }

    }, [dispatch, isOpen, rfqId, reassignType]);

    useEffect(() => {

        if (!selectedManager) return;

        dispatch(fetchMembersByManager({
            managerId: selectedManager.value,
            page: 1,
            per_page: 100
        }));

    }, [dispatch, selectedManager]);

    const membersData = associated_members || {};

    const handleClose = () => {
        reset({
            member: null,
            deadline: "",
            priority: priorities[1],
            notes: "",
        });
        setReassignType("associated");
        setSelectedDepartment(null);
        setSelectedManager(null);

        onClose();
    };

    const memberOptions = useMemo(() => {
        const options = [];
        departmentOrder.forEach((department) => {

            const members = membersData?.[department] || [];

            if (members.length > 0) {

                options.push({
                    label: department,
                    options: members.map((member) => ({
                        value: member.user_id,
                        label: member.name,
                        searchText: `${member?.name || ""} ${member?.designation || ""} ${member?.role || ""} ${department || ""}`.toLowerCase(),
                        memberData: { ...member, department },
                    })),
                });
            }
        });
        return options;
    }, [membersData]);

    const departmentOptions = useMemo(() => {
        return Object.keys(associated_managers || {}).map((dep) => ({
            value: dep,
            label: dep,
        }));
    }, [associated_managers]);

    const managerOptions = useMemo(() => {

        if (!selectedDepartment) return [];

        const departmentManagers = associated_managers[selectedDepartment.value]?.data || [];

        return departmentManagers.map((manager) => ({
            value: manager.user_id,
            label: manager.name,
            memberData: {
                ...manager,
                department: selectedDepartment.value,
            },
        }));

    }, [selectedDepartment, associated_managers]);

    const memberByManagerOptions = useMemo(() => {

        const members = manager_members?.data || [];

        return members.map((member) => ({
            value: member.user_id,
            label: member.name,
            memberData: {
                ...member,
                department: selectedDepartment?.value,
            },
        }));

    }, [manager_members, selectedDepartment]);

    const handleDepartmentChange = (department) => {

        setSelectedDepartment(department);
        setSelectedManager(null);

        setValue("member", null);
    };

    const handleManagerChange = (manager) => {

        setSelectedManager(manager);

        setValue("member", null);
    };

    const onSubmit = async (formData) => {

        try {

            // const payload = {
            //     rfq_id: rfqId,
            //     assigned_to: formData.member.memberData.user_id,
            //     type: formData.member.memberData.department.toLowerCase(),
            //     priority: formData.priority.value,
            //     deadline: formData.deadline,
            //     notes: formData.notes,
            // };

            const payload = {
                rfq_id: rfqId,
                manager_id:reassignType === "new" && selectedManager ? selectedManager.value: null,
                assigned_to: formData.member.memberData.user_id,
                type: (
                    formData.member.memberData.department ||
                    selectedDepartment?.value ||
                    ""
                ).toLowerCase(),
                priority: formData.priority.value,
                deadline: formData.deadline,
                notes: formData.notes,
            };

            const result = await dispatch(reassignRfqOrOrder(payload)).unwrap();

            if (result?.status) {

                Toaster.success(
                    result?.message ||
                    t("reassign_success", {
                        type: isOrderView ? "Order" : "RFQ"
                    })
                );

                handleClose();
                navigate(-1);

            } else {

                Toaster.error(result?.message || ct("somethingWentWrong"));
            }

        } catch (error) {

            Toaster.error(
                error?.message ||
                t("reassign_failed", {
                    type: isOrderView ? "Order" : "RFQ"
                })
            );
        }
    };

    const minDateTime = new Date().toISOString().slice(0, 16);

    const renderMemberOption = (data) => {

        const member = data.memberData;

        return (
            <div className="flex items-center gap-3 py-1">
                <Avatar
                    src={member?.profile_picture || "/img/default-avatar-profile-icon.avif"}
                    alt={member?.name}
                    size="sm"
                />

                <div>
                    <p className="text-sm font-semibold text-black">
                        {member?.name}
                    </p>

                    <p className="text-xs text-gray-500">
                        {member?.role} • {member?.designation}
                    </p>
                </div>
            </div>
        );
    };

    const handleReassignTypeChange = (type) => {

     setReassignType(type);
       setSelectedDepartment(null);
       setSelectedManager(null);

       reset({
           member: null,
           deadline: "",
           priority: priorities[1],
           notes: "",
       });
    };

    return (
        <Dialog
            open={isOpen}
            handler={handleClose}
            size="md"
            className="rounded-none md:rounded-2xl md:w-[640px]"
        >

            {loading && <ShowLoader />}

            <DialogHeader className="flex items-center gap-3 border-b px-5 py-3.5">

                <IconButton variant="text" onClick={handleClose}>
                    <ArrowLeftIcon className="h-5 w-5" />
                </IconButton>

                <Typography variant="h5" className="font-bold text-black">
                    {isOrderView
                        ? t("reassign_order_title")
                        : t("reassign_rfq_title_modal")}
                </Typography>

            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)}>

                <DialogBody className="px-5 py-4 max-h-[75vh] overflow-y-auto">

                    <div className="space-y-5">

                        {/* Radio Tabs */}

                        <div className="flex flex-wrap items-center gap-6 border-b pb-4">

                            <label className="flex items-center gap-2 cursor-pointer">

                                <input
                                    type="radio"
                                    name="reassignType"
                                    value="associated"
                                    checked={reassignType === "associated"}
                                    // onChange={() => setReassignType("associated")}
                                    onChange={() => handleReassignTypeChange("associated")}

                                    className="h-4 w-4 accent-black"
                                />

                                <span className="text-sm font-medium text-black">
                                    {t("reassignToAssociatedMember")}
                                </span>

                            </label>

                            <label className="flex items-center gap-2 cursor-pointer">

                                <input
                                    type="radio"
                                    name="reassignType"
                                    value="new"
                                    checked={reassignType === "new"}
                                    // onChange={() => setReassignType("new")}
                                    onChange={() => handleReassignTypeChange("new")}
                                    className="h-4 w-4 accent-black"
                                />

                                <span className="text-sm font-medium text-black">
                                    {t("reassignToNewMember")}
                                </span>

                            </label>

                        </div>

                        {/* Associated Member Flow */}

                        {reassignType === "associated" && (

                            <div>

                                <Typography
                                    variant="small"
                                    className="mb-2 font-medium text-black capitalize"
                                >
                                    {t("select_member_label")} <span className="text-red-500">*</span>
                                </Typography>

                                <Controller
                                    name="member"
                                    control={control}
                                    render={({ field }) => (

                                        <Select
                                            {...field}
                                            options={memberOptions}
                                            styles={customSelectStyles}
                                            placeholder={t("select_member_placeholder")}
                                            isClearable
                                            isSearchable
                                            filterOption={(option, inputValue) =>
                                                option?.data?.searchText?.includes(inputValue.toLowerCase())
                                            }
                                            components={{
                                                ClearIndicator: (props) => (
                                                    <components.ClearIndicator {...props}>
                                                        <XCircleIcon className="h-5 w-5 text-red-500 hover:text-red-700" />
                                                    </components.ClearIndicator>
                                                ),
                                            }}
                                            formatOptionLabel={renderMemberOption}
                                        />
                                    )}
                                />

                                {errors.member && (
                                    <p className="text-red-500 text-xs mt-1">
                                        {t(errors.member.message)}
                                    </p>
                                )}

                            </div>
                        )}

                        {/* New Member Flow */}

                        {reassignType === "new" && (

                            <div className="space-y-4">

                                {/* Department */}

                                <div>

                                    <Typography variant="small" className="mb-2 font-medium text-black capitalize">
                                        {t("department_label")} <span className="text-red-500">*</span>
                                    </Typography>

                                    <Select
                                        options={departmentOptions}
                                        styles={customSelectStyles}
                                        placeholder={t("select_department_placeholder")}
                                        value={selectedDepartment}
                                        onChange={handleDepartmentChange}
                                        isClearable
                                    />

                                </div>

                                {/* Manager */}

                                <div>

                                    <Typography variant="small" className="mb-2 font-medium text-black capitalize">
                                        {t("sales_manager_label")} <span className="text-red-500">*</span>
                                    </Typography>

                                    <Select
                                        options={managerOptions}
                                        styles={customSelectStyles}
                                        placeholder={t("select_sales_manager_placeholder")}
                                        value={selectedManager}
                                        onChange={handleManagerChange}
                                        isDisabled={!selectedDepartment}
                                        isClearable
                                        formatOptionLabel={renderMemberOption}
                                    />

                                </div>

                                {/* Member */}

                                <div>

                                    <Typography variant="small" className="mb-2 font-medium text-black">
                                        {t("select_member_label")} <span className="text-red-500">*</span>
                                    </Typography>

                                    <Controller
                                        name="member"
                                        control={control}
                                        render={({ field }) => (

                                            <Select
                                                {...field}
                                                options={memberByManagerOptions}
                                                styles={customSelectStyles}
                                                placeholder={t("select_member_placeholder")}
                                                isDisabled={!selectedManager}
                                                isClearable
                                                formatOptionLabel={renderMemberOption}
                                            />
                                        )}
                                    />

                                    {errors.member && (
                                        <p className="text-red-500 text-xs mt-1">
                                            {t(errors.member.message)}
                                        </p>
                                    )}

                                </div>

                            </div>
                        )}

                        {/* Selected User Preview */}

                        {selectedUser && (

                            <div className="bg-gray-50 border rounded-2xl p-4 flex items-center gap-4">

                                <Avatar
                                    src={selectedUser?.memberData?.profile_picture || "/img/default-avatar-profile-icon.avif"}
                                    alt={selectedUser?.memberData?.name}
                                    size="lg"
                                />

                                <div>

                                    <Typography className="font-semibold text-black">
                                        {selectedUser?.memberData?.name}
                                    </Typography>

                                    <Typography className="text-sm text-gray-600 capitalize">
                                        {selectedUser?.memberData?.designation || "Representative"}
                                    </Typography>

                                    <Typography className="text-xs text-gray-500 mt-1">
                                        {t("department_label")} :
                                        {selectedUser?.memberData?.department}
                                    </Typography>

                                </div>

                            </div>
                        )}

                        {/* Deadline + Priority */}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                            <div>

                                <Typography variant="small" className="mb-2 font-medium text-black capitalize">
                                    {t("deadline_label")} <span className="text-red-500">*</span>
                                </Typography>

                                <Controller
                                    name="deadline"
                                    control={control}
                                    render={({ field }) => (

                                        <Input
                                            {...field}
                                            type="datetime-local"
                                            min={minDateTime}
                                            className="!border-t-blue-gray-200 focus:!border-t-black"
                                            labelProps={{
                                                className: "before:content-none after:content-none"
                                            }}
                                        />
                                    )}
                                />

                                {errors.deadline && (
                                    <p className="text-red-500 text-xs mt-1">
                                        {t(errors.deadline.message)}
                                    </p>
                                )}

                            </div>

                            <div>

                                <Typography variant="small" className="mb-2 font-medium text-black capitalize">
                                    {t("priority_label")} <span className="text-red-500">*</span>
                                </Typography>

                                <Controller
                                    name="priority"
                                    control={control}
                                    render={({ field }) => (

                                        <Select
                                            {...field}
                                            options={priorities}
                                            styles={customSelectStyles}
                                            placeholder={t("select_priority_placeholder")}
                                        />
                                    )}
                                />

                                {errors.priority && (
                                    <p className="text-red-500 text-xs mt-1">
                                        {t(errors.priority.message)}
                                    </p>
                                )}

                            </div>

                        </div>

                        {/* Notes */}

                        <div>

                            <Typography variant="small" className="mb-2 font-medium text-black capitalize">
                                {t("notes_label")} <span className="text-red-500">*</span>
                            </Typography>

                            <Textarea
                                rows={4}
                                placeholder={t("note_placeholder")}
                                className="focus:border-black"
                                {...register("notes")}
                            />

                            {errors.notes && (
                                <p className="text-red-500 text-xs mt-1">
                                    {t(errors.notes.message)}
                                </p>
                            )}

                        </div>

                        {/* Notice */}

                        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">

                            <Typography className="text-sm font-semibold text-amber-900 mb-1">
                                {t("important_notice_title")}
                            </Typography>

                            <Typography className="text-sm text-amber-800 leading-relaxed">
                                {t("reassign_notice", {
                                    type: isOrderView ? "Order" : "RFQ"
                                })}
                            </Typography>

                        </div>

                    </div>

                </DialogBody>

                <DialogFooter className="border-t px-5 py-3.5 flex gap-3">

                    <Button
                        type="button"
                        variant="outlined"
                        color="gray"
                        onClick={handleClose}
                        className="flex-1 rounded-xl border-black text-black"
                    >
                        {ct("cancel")}
                    </Button>

                    <Button
                        type="submit"
                        className="flex-1 rounded-xl bg-black text-white"
                    >
                        {t("reassign_button")}
                    </Button>

                </DialogFooter>

            </form>

        </Dialog>
    );
}